import React, { useState, useEffect } from 'react'
import { Button, Card, Table, Checkbox, Input, Form, Space, Tag, Typography, message, Tooltip, Spin, Modal, Tabs, Tree } from 'antd'
import { apiRequest } from '../../helpers/apiRequest'
import TableContent from '../../components/TableContent'
import {
  SearchOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  DeleteOutlined,
  PartitionOutlined,
  ExportOutlined,
  FolderOutlined,
} from '@ant-design/icons'
import { Content } from 'antd/es/layout/layout'
import { useLanguage } from '../../components/context/LanguageContext'

const { Title, Text } = Typography
const url = 'connector/url'
const item = 'URL Connector'
const items = 'URL Connectors'

const UrlConnector = () => {
  const { t } = useLanguage() // Add translation support

  const [selectedConnector, setSelectedConnector] = useState(null)
  const [crawledUrls, setCrawledUrls] = useState([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [loading, setLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)
  const [connectorStatus, setConnectorStatus] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  // Content modal state
  const [contentModalVisible, setContentModalVisible] = useState(false)
  const [activeTab, setActiveTab] = useState('text')
  const [currentContent, setCurrentContent] = useState({
    title: '',
    url: '',
    content: '',
    htmlContent: '',
  })

  // Sitemap modal state
  const [sitemapModalVisible, setSitemapModalVisible] = useState(false)
  const [sitemapLoading, setSitemapLoading] = useState(false)
  const [sitemapData, setSitemapData] = useState({
    tree: [],
    totalNodes: 0,
  })

  // Handle when a connector is selected for URL management
  const handleManageUrls = connector => {
    if (!connector || !connector.id) {
      setSelectedConnector(null)
      return
    }

    setSelectedConnector(connector)
    fetchCrawledUrls(connector.id, 1)
    fetchConnectorStatus(connector.id)
  }

  // Fetch connector status
  const fetchConnectorStatus = async connectorId => {
    setStatusLoading(true)
    try {
      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${connectorId}/status`, 'GET')
      if (response && response.data) {
        setConnectorStatus(response.data)
      }
    } catch (error) {
      console.error('Error fetching connector status:', error)
      message.error('Failed to get connector status')
    } finally {
      setStatusLoading(false)
    }
  }

  // Fetch crawled URLs for a connector
  const fetchCrawledUrls = async (connectorId, page = 1, pageSize = 10, search = '') => {
    setLoading(true)
    try {
      // Add search parameter if provided
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : ''
      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${connectorId}/urls?page=${page}&limit=${pageSize}${searchParam}`, 'GET')

      if (response && response.data && response.data.items) {
        setCrawledUrls(response.data.items)
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.data.total || 0,
        })
      }
    } catch (error) {
      console.error('Error fetching crawled URLs:', error)
      message.error('Failed to get crawled URLs')
    } finally {
      setLoading(false)
    }
  }

  // Handle refreshing the URLs list
  const handleRefreshUrls = () => {
    if (!selectedConnector) {
      message.error(t('No connector selected'))
      return
    }

    message.info(t('Refreshing URLs list...'))
    fetchCrawledUrls(selectedConnector.id, pagination.current, pagination.pageSize, searchText)
  }

  // Start crawling URLs for a connector
  const startCrawl = async () => {
    if (!selectedConnector) {
      message.error('No connector selected')
      return
    }

    try {
      setStatusLoading(true)
      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${selectedConnector.id}/crawl`, 'GET')
      if (response && response.ok) {
        message.success('Crawl started successfully')
        fetchConnectorStatus(selectedConnector.id)
      } else {
        message.error('Failed to start crawl')
      }
    } catch (error) {
      console.error('Error starting crawl:', error)
      message.error('Failed to start crawl')
    } finally {
      setStatusLoading(false)
    }
  }

  // Fetch sitemap data for the connector
  const fetchSitemap = async () => {
    if (!selectedConnector) {
      message.error('No connector selected')
      return
    }

    try {
      setSitemapLoading(true)
      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${selectedConnector.id}/sitemap`, 'GET')

      if (response && response.ok && response.data) {
        setSitemapData(response.data)
        setSitemapModalVisible(true)
      } else {
        message.error('Failed to load sitemap data')
      }
    } catch (error) {
      console.error('Error fetching sitemap:', error)
      message.error('Failed to load sitemap')
    } finally {
      setSitemapLoading(false)
    }
  }

  // Toggle URL selection
  const toggleUrlSelection = async (urlId, selected) => {
    if (!selectedConnector) {
      message.error('No connector selected')
      return
    }

    try {
      // Optimistically update the UI to show immediate feedback
      setCrawledUrls(prevUrls => prevUrls.map(u => (u.id === urlId ? { ...u, selected } : u)))

      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${selectedConnector.id}/urls/${urlId}`, 'PATCH', { selected })

      if (response && response.ok) {
        message.success(selected ? 'URL selected' : 'URL deselected')
        // Refresh connector status to update counts
        fetchConnectorStatus(selectedConnector.id)
      } else {
        // If failed, revert the optimistic update
        message.error('Failed to update URL selection')
        fetchCrawledUrls(selectedConnector.id, pagination.current, pagination.pageSize)
      }
    } catch (error) {
      console.error('Error toggling URL selection:', error)
      message.error('Failed to update URL selection')
      fetchCrawledUrls(selectedConnector.id, pagination.current, pagination.pageSize)
    }
  }

  // Handle pagination change
  const handleTableChange = pagination => {
    // Keep the search parameter when changing pages
    fetchCrawledUrls(selectedConnector.id, pagination.current, pagination.pageSize, searchText)
  }

  // Handle opening the content modal
  const showContentModal = record => {
    const htmlContent = record.content || t('Loading content...')
    const textContent = record.textContent || record.content || t('Loading content...')

    setCurrentContent({
      id: record.id,
      title: record.title || t('Page Content'),
      url: record.url,
      content: textContent,
      htmlContent: htmlContent,
      loading: false,
    })
    setContentModalVisible(true)
    setActiveTab('text') // Reset to text tab by default

    // If content is short/partial or missing, load full content
    if (!record.content || record.content.length < 500) {
      refreshPageContent(record.id, record.url)
    }
  }

  // Refresh page content from URL
  const refreshPageContent = async (id, pageUrl) => {
    if (!id || !pageUrl || !selectedConnector) return

    setCurrentContent(prev => ({ ...prev, loading: true }))

    try {
      // First try to get content from backend
      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${selectedConnector.id}/content/${id}`, 'GET')

      if (response?.ok && response?.data) {
        const htmlContent = response.data.content
        // Use the server-provided text content or fallback to basic HTML tag removal
        const textContent =
          response.data.textContent ||
          (htmlContent
            ? htmlContent
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
            : '')

        // Update displayed content
        setCurrentContent(prev => ({
          ...prev,
          content: textContent,
          htmlContent: htmlContent,
          loading: false,
        }))

        // Also update the content in the table data
        setCrawledUrls(prev =>
          prev.map(item =>
            item.id === id
              ? {
                  ...item,
                  content: htmlContent,
                  textContent: textContent,
                }
              : item
          )
        )

        return
      }

      // If backend doesn't have content or can't provide it,
      // try to fetch the page directly (this might not work due to CORS)
      try {
        const pageResponse = await fetch(pageUrl, {
          headers: {
            Accept: 'text/html',
          },
          mode: 'cors',
        })

        if (pageResponse.ok) {
          const html = await pageResponse.text()
          const textContent = html
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()

          setCurrentContent(prev => ({
            ...prev,
            content: textContent,
            htmlContent: html,
            loading: false,
          }))

          // If we got content directly, update the crawled URLs list too
          setCrawledUrls(prev =>
            prev.map(item =>
              item.id === id
                ? {
                    ...item,
                    content: html,
                    textContent: textContent,
                  }
                : item
            )
          )
        } else {
          throw new Error('Failed to fetch page content')
        }
      } catch (fetchError) {
        const errorMessage = t('Unable to fetch page content. The server may need to recrawl this URL.')
        setCurrentContent(prev => ({
          ...prev,
          content: errorMessage,
          htmlContent: errorMessage,
          loading: false,
        }))
      }
    } catch (error) {
      console.error('Error refreshing page content:', error)
      const errorMessage = t('Error loading content')
      setCurrentContent(prev => ({
        ...prev,
        content: errorMessage,
        htmlContent: errorMessage,
        loading: false,
      }))
    }
  }

  // Refresh content for multiple pages
  const refreshMultiplePages = async ids => {
    if (!ids || !ids.length || !selectedConnector) return

    message.loading(t('Refreshing content for {{count}} pages...').replace('{{count}}', ids.length), 0)

    try {
      // Call refresh for each ID
      await Promise.all(
        ids.map(id => {
          const urlItem = crawledUrls.find(item => item.id === id)
          if (urlItem) {
            return refreshPageContent(id, urlItem.url)
          }
          return Promise.resolve()
        })
      )

      message.destroy()
      message.success(t('Content refreshed for {{count}} pages').replace('{{count}}', ids.length))
    } catch (error) {
      console.error('Error refreshing multiple pages:', error)
      message.destroy()
      message.error(t('Error refreshing some pages'))
    }
  }

  // Handle batch selection actions
  const handleBatchSelect = async selected => {
    if (!selectedRowKeys.length) {
      message.info(t('Please select at least one URL'))
      return
    }

    // Show loading message
    message.loading(t('Processing {{count}} URLs...').replace('{{count}}', selectedRowKeys.length), 0)

    try {
      // If we have more IDs than visible in the current page
      // we'll need to fetch info about all of them
      let allItems = crawledUrls

      // If we have more IDs than items in the current page,
      // we need to fetch all items matching the current filter
      if (selectedRowKeys.length > crawledUrls.length) {
        const searchParam = searchText ? `&search=${encodeURIComponent(searchText)}` : ''
        const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${selectedConnector.id}/urls?page=1&limit=10000${searchParam}`, 'GET')

        if (response && response.data && response.data.items) {
          allItems = response.data.items
        }
      }

      // For each selected key, find its corresponding item and process it
      const itemsToProcess = selectedRowKeys.map(key => {
        // First try to find the item in the currently loaded data
        let urlItem = allItems.find(item => item.id === key)

        // If we can't find it (shouldn't happen, but just in case),
        // create a minimum viable item with just the ID
        if (!urlItem) {
          urlItem = { id: key }
        }

        return urlItem
      })

      // Process each item in batches to avoid overloading the API
      const batchSize = 25
      let processed = 0

      for (let i = 0; i < itemsToProcess.length; i += batchSize) {
        const batch = itemsToProcess.slice(i, i + batchSize)

        await Promise.all(batch.map(item => toggleUrlSelection(item.id, selected)))

        processed += batch.length
        message.loading(t('Processing... {{done}}/{{total}} complete').replace('{{done}}', processed).replace('{{total}}', itemsToProcess.length), 0)
      }

      message.destroy()
      message.success(
        selected
          ? t('{{count}} URLs have been marked as selected').replace('{{count}}', itemsToProcess.length)
          : t('{{count}} URLs have been marked as deselected').replace('{{count}}', itemsToProcess.length)
      )

      // Refresh data to show the updated state
      fetchConnectorStatus(selectedConnector.id)
      fetchCrawledUrls(selectedConnector.id, pagination.current, pagination.pageSize, searchText)

      // Clear selection
      setSelectedRowKeys([])
    } catch (error) {
      console.error('Error in batch operation:', error)
      message.destroy()
      message.error(t('Failed to update some URLs'))
    }
  }

  // Delete a single URL
  const deleteUrl = async urlId => {
    if (!selectedConnector) {
      message.error(t('No connector selected'))
      return false
    }

    try {
      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${selectedConnector.id}/urls/${urlId}`, 'DELETE')

      if (response && response.ok) {
        // Update local state to remove the URL
        setCrawledUrls(prevUrls => prevUrls.filter(item => item.id !== urlId))

        // Refresh connector status to update counts
        fetchConnectorStatus(selectedConnector.id)
        return true
      } else {
        throw new Error(response?.message || t('Failed to delete URL'))
      }
    } catch (error) {
      console.error(`Error deleting URL with ID ${urlId}:`, error)
      return false
    }
  }

  // Handle batch deletion
  const handleBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.info(t('Please select at least one URL'))
      return
    }

    // Confirm deletion
    Modal.confirm({
      title: t('Confirm Deletion'),
      content: t('Are you sure you want to delete the selected URLs? This action cannot be undone.'),
      okText: t('Yes, Delete'),
      okType: 'danger',
      cancelText: t('Cancel'),
      onOk: async () => {
        message.loading(t('Deleting URLs...'), 0)

        // Process each selected URL
        const results = await Promise.allSettled(selectedRowKeys.map(urlId => deleteUrl(urlId)))

        message.destroy()

        // Count successes and failures
        const successes = results.filter(r => r.status === 'fulfilled' && r.value === true).length
        const failures = results.length - successes

        if (successes > 0) {
          message.success(t('{{count}} URLs deleted successfully').replace('{{count}}', successes))
        }

        if (failures > 0) {
          message.error(t('Failed to delete {{count}} URLs').replace('{{count}}', failures))
        }

        // Refresh data and clear selection
        fetchCrawledUrls(selectedConnector.id, pagination.current, pagination.pageSize, searchText)
        setSelectedRowKeys([])
      },
    })
  }

  // Function to fetch all URL IDs for the current filter
  const fetchAllFilteredIds = async () => {
    if (!selectedConnector) return []

    setLoading(true)
    try {
      // Fetch with a large limit to get most/all results in one request
      // The search parameter ensures we're only getting filtered results
      const searchParam = searchText ? `&search=${encodeURIComponent(searchText)}` : ''
      const response = await apiRequest(`${process.env.REACT_APP_API}${url}/${selectedConnector.id}/urls?page=1&limit=10000${searchParam}`, 'GET')

      if (response && response.data && response.data.items) {
        return response.data.items.map(item => item.id)
      }
      return []
    } catch (error) {
      console.error('Error fetching all filtered IDs:', error)
      message.error(t('Failed to get all IDs'))
      return []
    } finally {
      setLoading(false)
    }
  }

  // Table row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: keys => setSelectedRowKeys(keys),
    selections: [
      // Standard selection options
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,

      // Custom selection options
      {
        key: 'select-all-on-page',
        text: t('Select all on current page'),
        onSelect: () => {
          const keys = crawledUrls.map(item => item.id)
          setSelectedRowKeys(prevKeys => {
            // Merge with existing keys but avoid duplicates
            const newKeys = [...new Set([...prevKeys, ...keys])]
            return newKeys
          })
        },
      },
      {
        key: 'select-all-data',
        text: t('Select all data'),
        onSelect: async () => {
          message.loading(t('Loading all IDs...'), 0)
          const allIds = await fetchAllFilteredIds()
          message.destroy()

          if (allIds.length > 0) {
            setSelectedRowKeys(allIds)
            message.success(t('Selected all {{count}} items').replace('{{count}}', allIds.length))
          } else {
            message.warning(t('No items to select'))
          }
        },
      },
      {
        key: 'select-all-filtered',
        text: t('Select all filtered results'),
        onSelect: async () => {
          if (!searchText) {
            message.info(t('No filter applied'))
            return
          }

          message.loading(t('Loading filtered IDs...'), 0)
          const filteredIds = await fetchAllFilteredIds()
          message.destroy()

          if (filteredIds.length > 0) {
            setSelectedRowKeys(filteredIds)
            message.success(t('Selected all {{count}} filtered items').replace('{{count}}', filteredIds.length))
          } else {
            message.warning(t('No items match the filter'))
          }
        },
      },
    ],
  }

  // Table columns for the crawled URLs
  const columns = [
    {
      title: t('URL'),
      dataIndex: 'url',
      key: 'url',
      render: text => (
        <a href={text} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ),
    },
    {
      title: t('Title'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('Crawl Time'),
      dataIndex: 'crawlTime',
      key: 'crawlTime',
      render: text => (text ? new Date(text).toLocaleString() : t('N/A')),
    },
    {
      title: t('Action'),
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {/* Selection toggle icon */}
          <Tooltip title={record.selected ? t('Deselect') : t('Select')}>
            {record.selected ? (
              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} onClick={() => toggleUrlSelection(record.id, false)} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} onClick={() => toggleUrlSelection(record.id, true)} />
            )}
          </Tooltip>

          {/* Content view icon */}
          <Tooltip title={t('View Content')}>
            <EyeOutlined style={{ color: '#1890ff', fontSize: '18px' }} onClick={() => showContentModal(record)} />
          </Tooltip>

          {/* Delete URL icon */}
          <Tooltip title={t('Delete URL')}>
            <DeleteOutlined
              style={{ color: '#ff4d4f', fontSize: '18px' }}
              onClick={() => {
                Modal.confirm({
                  title: t('Confirm Deletion'),
                  content: t('Are you sure you want to delete this URL? This action cannot be undone.'),
                  okText: t('Yes, Delete'),
                  okType: 'danger',
                  cancelText: t('Cancel'),
                  onOk: () =>
                    deleteUrl(record.id).then(success => {
                      if (success) {
                        message.success(t('URL deleted successfully'))
                        // List is automatically updated since we modified setCrawledUrls in deleteUrl
                      } else {
                        message.error(t('Failed to delete URL'))
                      }
                    }),
                })
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  // Render a sitemap tree node recursively
  const renderSitemapNode = node => {
    const titleElement = (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginRight: 'auto', maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.title || node.url}
        </div>
        <div>
          <Tooltip title={t('Open URL in new tab')}>
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={e => {
                e.stopPropagation()
                window.open(node.url, '_blank')
              }}
            />
          </Tooltip>
          <Tooltip title={t('View Content')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={e => {
                e.stopPropagation()
                // Find the original record to get full content
                const record = crawledUrls.find(r => r.id === node.id)
                if (record) {
                  showContentModal(record)
                } else {
                  message.warning(t('Content not found'))
                }
              }}
            />
          </Tooltip>
        </div>
      </div>
    )

    // Determine if this node has children
    if (node.children && node.children.length > 0) {
      return {
        title: titleElement,
        key: `node-${node.id}`,
        icon: <FolderOutlined />,
        children: node.children.map(child => renderSitemapNode(child)),
      }
    } else {
      return {
        title: titleElement,
        key: `node-${node.id}`,
        icon: <FileTextOutlined />,
      }
    }
  }

  // Process sitemap data into tree structure for Tree component
  const getTreeData = () => {
    if (!sitemapData || !sitemapData.tree) return []
    return sitemapData.tree.map(node => renderSitemapNode(node))
  }

  // Add custom buttons to the table
  const tableExtensions = {
    renderAdditionalActions: record => <Button onClick={() => handleManageUrls(record)}>{t('Manage URLs')}</Button>,
    renderAdditionalExtensions: () => null, // Remove this button as it's already in the manage URLs section
  }

  return (
    <>
      {/* Sitemap Modal */}
      <Modal
        title={
          <div>
            <PartitionOutlined style={{ marginRight: 8 }} />
            {t('Sitemap')} - {selectedConnector?.name || ''}
          </div>
        }
        open={sitemapModalVisible}
        onCancel={() => setSitemapModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setSitemapModalVisible(false)}>
            {t('Close')}
          </Button>,
        ]}
      >
        {sitemapLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '10px' }}>{t('Loading sitemap...')}</div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              {t('Total Pages')}: {sitemapData.totalNodes}
            </div>
            <div
              style={{
                maxHeight: '60vh',
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
                padding: '10px',
              }}
            >
              <Tree defaultExpandAll={false} selectable={false} treeData={getTreeData()} />
            </div>
          </div>
        )}
      </Modal>

      {/* Content Modal */}
      <Modal
        title={
          <div>
            <div>{currentContent.title}</div>
            <div>
              <a href={currentContent.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px' }}>
                {currentContent.url}
              </a>
            </div>
          </div>
        }
        open={contentModalVisible}
        onCancel={() => setContentModalVisible(false)}
        footer={[
          <Button
            key="refresh"
            type="primary"
            icon={<SyncOutlined />}
            loading={currentContent.loading}
            onClick={() => refreshPageContent(currentContent.id, currentContent.url)}
          >
            {t('Refresh Content')}
          </Button>,
          <Button key="close" onClick={() => setContentModalVisible(false)}>
            {t('Close')}
          </Button>,
        ]}
        width={800}
      >
        {currentContent.loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '10px' }}>{t('Loading content...')}</div>
          </div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'text',
                label: t('Text Content'),
                children: (
                  <div
                    style={{
                      maxHeight: '60vh',
                      overflowY: 'auto',
                      padding: '10px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px',
                      backgroundColor: '#fafafa',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                    }}
                  >
                    {currentContent.content}
                  </div>
                ),
              },
              {
                key: 'html',
                label: t('HTML Content'),
                children: (
                  <div
                    style={{
                      maxHeight: '60vh',
                      overflowY: 'auto',
                      padding: '10px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px',
                      backgroundColor: '#fafafa',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                    }}
                  >
                    {currentContent.htmlContent}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Modal>

      <Space direction="vertical" style={{ width: '100%' }}>
        <TableContent url={url} type={'connector-url'} item={item} items={items} extensions={tableExtensions} onRowSelect={handleManageUrls} />

        {selectedConnector ? (
          <Card>
            <Title level={4}>
              {t('Manage URLs')} - {selectedConnector?.name || ''}
            </Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Connector Status & Controls */}
              <Card style={{ marginBottom: 16 }}>
                <Space>
                  <Button type="primary" onClick={startCrawl} loading={statusLoading} disabled={connectorStatus?.crawlInProgress}>
                    {t('Start Crawl')}
                  </Button>

                  <Button
                    onClick={() => {
                      fetchConnectorStatus(selectedConnector?.id)
                      fetchCrawledUrls(selectedConnector.id, pagination.current, pagination.pageSize, searchText)
                    }}
                    disabled={!selectedConnector || loading || statusLoading}
                    icon={<SyncOutlined spin={loading || statusLoading} />}
                    loading={loading || statusLoading}
                  >
                    {t('Refresh')}
                  </Button>

                  <Button onClick={fetchSitemap} disabled={!selectedConnector || statusLoading} icon={<PartitionOutlined />}>
                    {t('Sitemap')}
                  </Button>
                </Space>

                {statusLoading ? (
                  <Spin style={{ marginLeft: 16 }} />
                ) : connectorStatus ? (
                  <Space style={{ marginLeft: 16 }}>
                    <Text>
                      {t('Status')}:{' '}
                      {connectorStatus.crawlInProgress ? (
                        <Tag icon={<SyncOutlined spin />} color="processing">
                          {t('Crawling')}
                        </Tag>
                      ) : (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                          {t('Ready')}
                        </Tag>
                      )}
                    </Text>
                    <Text>
                      {t('Last Crawl')}: {connectorStatus.lastCrawlTime ? new Date(connectorStatus.lastCrawlTime).toLocaleString() : t('Never')}
                    </Text>
                    <Text>
                      {t('URLs Crawled')}: {connectorStatus.urlsCrawled}
                    </Text>
                    <Text>
                      {t('URLs Pending')}: {connectorStatus.urlsPending}
                    </Text>
                    <Text>
                      {t('URLs Selected')}: {connectorStatus.urlsSelected}
                    </Text>
                  </Space>
                ) : null}
              </Card>

              {/* Batch Action Controls */}
              <Card style={{ marginBottom: 16 }}>
                <Space>
                  <Text strong>{t('Batch Actions')}:</Text>
                  <Button type="primary" onClick={() => handleBatchSelect(true)} disabled={selectedRowKeys.length === 0}>
                    {t('Select All Checked')}
                  </Button>
                  <Button onClick={() => handleBatchSelect(false)} disabled={selectedRowKeys.length === 0}>
                    {t('Deselect All Checked')}
                  </Button>
                  <Button
                    type="default"
                    icon={<SyncOutlined />}
                    onClick={() => refreshMultiplePages(selectedRowKeys)}
                    disabled={selectedRowKeys.length === 0}
                  >
                    {t('Refresh Content')}
                  </Button>
                  <Button type="danger" icon={<DeleteOutlined />} onClick={handleBatchDelete} disabled={selectedRowKeys.length === 0}>
                    {t('Delete Selected')}
                  </Button>
                  <Text strong>
                    {selectedRowKeys.length} {t('items selected')}
                  </Text>
                </Space>
              </Card>

              {/* Search */}
              <Input.Search
                placeholder={t('Search URLs, titles, descriptions...')}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onSearch={() => {
                  // Reset to page 1 when searching
                  setPagination(prev => ({ ...prev, current: 1 }))
                  fetchCrawledUrls(selectedConnector.id, 1, pagination.pageSize, searchText)
                }}
                enterButton={t('Search')}
                allowClear
                style={{ marginBottom: 16 }}
              />

              {/* URLs Table */}
              <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={crawledUrls}
                rowKey="id"
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
              />
            </Space>
          </Card>
        ) : null}
      </Space>
    </>
  )
}

export default UrlConnector
